<template>
  <div
    class="d-flex"
    style="height: 100%;"
  >
    <div v-if="!isZoomIn">
      <div
        v-for="(marginTop, id) in moveBtnMarginTop"
        :key="id"
        :style="{ marginTop }"
        class="d-flex flex-column justify-space-around container-move"
      >
        <v-btn
          v-if="id > 0"
          small
          icon
          @click="move(id, true)"
        >
          <v-icon>mdi-chevron-up</v-icon>
        </v-btn>
        <v-btn
          v-if="id < moveBtnMarginTop.length - 1"
          small
          icon
          @click="move(id, false)"
        >
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="!isZoomIn">
      <v-btn
        v-for="(marginTop, id) in zoomBtnMarginTop"
        :key="id"
        :style="{ marginTop }"
        class="btn-zoom"
        small
        icon
        @click="zoomIn(id)"
      >
        <v-icon>mdi-arrow-expand</v-icon>
      </v-btn>
    </div>

    <v-btn
      v-if="isZoomIn"
      class="btn-zoom"
      small
      icon
      @click="zoomOut"
    >
      <v-icon>mdi-arrow-collapse</v-icon>
    </v-btn>

    <ChartValues
      v-for="(marginTop, id) in zoomBtnMarginTop"
      :key="id"
      :style="{marginTop, display: (panelVisible[id] ? 'block' : 'none')}"
      :type="panelNames[id]"
      :visible="panelVisible[id]"
      :data="currentDataPoint"
      :prev-data="prevDataPoint"
    />

    <v-slider
      ref="slider"
      v-model="sliderValue"
      hide-details
      vertical
      color="black"
      track-color="black"
      class="size-slider"
      :disabled="isZoomIn"
    />

    <div
      ref="chartContainer"
      class="lw-chart"
    />
  </div>
</template>

<script>
import { computed, getCurrentInstance, ref } from '@vue/composition-api'
import { createChart } from 'lightweight-charts'
import { evaluate } from '../dsl/evaluator'
import { adwinProgram } from '../dsl/programs'
import {
  baseChartOptions, candleSeriesOptions, priceLine, timezoneOffset,
} from '../utils/chartConstants'
import { applyTick } from '../utils/rawData'
import useChartData from '../composables/useChartData'
import useChartLayout from '../composables/useChartLayout'
import useChartSync from '../composables/useChartSync'
import useChartZoom from '../composables/useChartZoom'
import useChartMove from '../composables/useChartMove'
import ChartValues from './ChartValues.vue'

const adwinCharts = [null, null]
const adwinSeries = { candle: null, adwin: null }
const adwinTimeIndexes = {}

const adwinSeriesOptions = {
  color: 'brown',
  lineWidth: 1,
  priceLineVisible: false,
}

export default {
  components: { ChartValues },

  props: {
    resolution: {
      type: String,
      default: '1D',
    },
  },

  setup(_, { emit }) {
    const ins = getCurrentInstance()

    const getDataPoint = (currentData, index) => ({
      candle: currentData.candle?.[index],
      adwin: currentData.adwin?.[index],
    })

    const chartData = useChartData({ getDataPoint })

    const sliderValue = ref(60)

    // v-slider vertical: value 0 = bottom, 100 = top. Value N means top panel is (100-N)% height.
    const panelRatios = computed(() => [(100 - sliderValue.value) / 100, sliderValue.value / 100])

    const zoom = useChartZoom({
      charts: adwinCharts,
      sliderValue,
      zoomedSliderValueFor: chartId => (chartId === 0 ? 0 : 100),
    })

    const layout = useChartLayout({
      getChartContainer: () => ins.proxy.$refs.chartContainer,
      getSlider: () => ins.proxy.$refs.slider,
      charts: adwinCharts,
      panelRatios,
    })

    const sync = useChartSync({
      charts: adwinCharts,
      getSeriesByPanelId: idx => (ins.proxy.panelNames[idx] === 'candle' ? adwinSeries.candle : adwinSeries.adwin),
      timeIndexes: adwinTimeIndexes,
      currentTimeIndex: chartData.currentTimeIndex,
      emit,
    })

    const moveComposable = useChartMove({
      charts: adwinCharts,
      swapPanelNames: (id, swapId) => {
        const names = [...ins.proxy.panelNames];
        [names[id], names[swapId]] = [names[swapId], names[id]]
        ins.proxy.panelNames = names
      },
      initCharts: () => ins.proxy.initCharts(),
      setChartsHeight: layout.setChartsHeight,
      setChartsWidth: layout.setChartsWidth,
      replayData: () => { if (ins.proxy.rawData.t) ins.proxy.updateChart(ins.proxy.rawData) },
    })

    return {
      sliderValue,
      ...chartData,
      ...zoom,
      ...layout,
      ...sync,
      ...moveComposable,
    }
  },

  data() {
    return {
      panelNames: ['candle', 'adwin'],
    }
  },

  computed: {
    panelVisible() {
      if (!this.isZoomIn) return [true, true]

      return this.sliderValue === 0 ? [true, false] : [false, true]
    },
  },

  watch: {
    rawData(data) {
      this.updateChart(data)
    },
  },

  mounted() {
    this.initCharts()
  },

  beforeDestroy() {
    adwinCharts.forEach(chart => chart && chart.remove())
    adwinCharts[0] = null
    adwinCharts[1] = null
    adwinSeries.candle = null
    adwinSeries.adwin = null
  },

  methods: {
    initCharts() {
      const container = this.$refs.chartContainer
      const lastIdx = adwinCharts.length - 1
      adwinCharts.forEach((_, idx) => {
        const options = {
          ...baseChartOptions,
          timeScale: { ...baseChartOptions.timeScale, visible: idx === lastIdx },
        }
        adwinCharts[idx] = createChart(container, options)
      })

      adwinCharts.forEach((chart, idx) => {
        if (this.panelNames[idx] === 'candle') {
          adwinSeries.candle = chart.addCandlestickSeries(candleSeriesOptions)
        } else {
          adwinSeries.adwin = chart.addLineSeries(adwinSeriesOptions)
          adwinSeries.adwin.createPriceLine(priceLine(0, 1))
          adwinSeries.adwin.createPriceLine(priceLine(100, 2))
          adwinSeries.adwin.createPriceLine(priceLine(-100, 2))
        }
      })

      adwinCharts.forEach((chart, idx) => {
        this.bindSync(chart, idx)
      })
    },

    pushData(input) {
      const dt = applyTick(this.rawData, input, this.resolution)
      if (dt) this.rawData = dt
    },

    updateChart(raw) {
      const {
        t = [], o = [], h = [], l = [], c = [], v = [],
      } = raw

      const candleData = t.map((time, idx) => ({
        time: time + timezoneOffset,
        open: o[idx],
        high: h[idx],
        low: l[idx],
        close: c[idx],
      }))

      const result = evaluate(adwinProgram, {
        open: o, high: h, low: l, close: c, volume: v,
      })

      const adwinData = t.map((time, idx) => ({
        time: time + timezoneOffset,
        value: result.adwinDoScope[idx],
      }))

      candleData.forEach((d, idx) => { adwinTimeIndexes[d.time] = idx })
      this.currentData = { candle: candleData, adwin: adwinData }

      adwinSeries.candle.setData(candleData)
      adwinSeries.adwin.setData(adwinData)

      const { length } = t
      if (length) {
        adwinCharts[0].timeScale().setVisibleLogicalRange({ from: length - 50, to: length })
      }
    },

  },
}
</script>

<style scoped lang="scss" src="./_chartShared.scss"></style>
